/**
 * Filter inputs specific to the leads list: catalog-backed multi-selects and
 * facet-backed selects (distinct countries/regions/statuses/languages).
 */

import { useEffect, useState } from 'react';
import {
  SelectArrayInput,
  SelectInput,
  useGetList,
} from 'react-admin';
import { SxProps } from '@mui/material';
import { httpClient } from '@/utils/httpClient';

const compactSx: SxProps = {
  width: { xs: '100%', sm: 'auto' },
  minWidth: 160,
  '& .MuiInputBase-root': {
    minHeight: '30px !important',
    fontSize: '0.78rem',
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.78rem',
  },
};

interface LookupChoice {
  id: number;
  name: string;
}

/**
 * Multi-select filter whose choices come from a catalog resource
 * (tags, job-titles, hotel-chains...).
 */
export const LookupArrayFilter = ({
  source,
  resource,
  label,
}: {
  source: string;
  resource: string;
  label: string;
}) => {
  const { data } = useGetList<LookupChoice>(resource, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });
  return (
    <SelectArrayInput
      source={source}
      label={label}
      choices={data ?? []}
      optionText="name"
      optionValue="id"
      size="small"
      variant="outlined"
      helperText={false}
      sx={compactSx}
    />
  );
};

interface LeadFacets {
  countries: string[];
  regions: string[];
  statuses: string[];
  languages: string[];
}

const EMPTY_FACETS: LeadFacets = { countries: [], regions: [], statuses: [], languages: [] };

/** Canonical pipeline statuses; facets add whatever else exists in data. */
export const CANONICAL_STATUSES = ['Nuevo', 'Contactado', 'Interesado', 'Cliente', 'Descartado'];

export const useLeadFacets = (): LeadFacets => {
  const [facets, setFacets] = useState<LeadFacets>(EMPTY_FACETS);

  useEffect(() => {
    let cancelled = false;
    httpClient
      .get<LeadFacets>('/leads/facets')
      .then((response) => {
        if (!cancelled) setFacets(response.data);
      })
      .catch(() => {
        /* dropdowns simply stay empty */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return facets;
};

const toChoices = (values: string[]) => values.map((v) => ({ id: v, name: v }));

export const FacetArrayFilter = ({
  source,
  label,
  values,
}: {
  source: string;
  label: string;
  values: string[];
}) => (
  <SelectArrayInput
    source={source}
    label={label}
    choices={toChoices(values)}
    size="small"
    variant="outlined"
    helperText={false}
    sx={compactSx}
  />
);

export const FacetSelectFilter = ({
  source,
  label,
  values,
}: {
  source: string;
  label: string;
  values: string[];
}) => (
  <SelectInput
    source={source}
    label={label}
    choices={toChoices(values)}
    size="small"
    variant="outlined"
    helperText={false}
    sx={compactSx}
  />
);
